import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useJwt, useRootPath } from "~/stores/sessionStore";

interface Preview {
  id: number;
  status?: string;
  preview_url?: string | null;
  /** Sanitized build failure (server-scrubbed). Present only when the build failed. */
  build_error?: string | null;
}

/**
 * Client-side backstop deadline for a single build. The server already caps a
 * build at BUILD_TIMEOUT_SECONDS (300s) and, on breach, fails the WebsitePreview
 * with a real sanitized error — so in the normal case the *server's* failure
 * wins this race and the user sees the actual build error. This deadline sits
 * comfortably above that ceiling and only fires when a job is genuinely lost
 * (worker died, Redis dropped it, status stuck at "pending"/"building" forever),
 * turning an otherwise-perpetual loading spinner into an actionable error.
 */
const BUILD_GIVE_UP_MS = 360_000;
const GIVE_UP_MESSAGE =
  "The preview is taking longer than expected. Please try again.";

// Dev-only override so the give-up race can be reproduced without a 6-minute
// wait: localStorage.setItem("l10:giveUpMs", "10000") then reload.
function giveUpMs(): number {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = Number(window.localStorage.getItem("l10:giveUpMs"));
    if (override > 0) return override;
  }
  return BUILD_GIVE_UP_MS;
}

/**
 * Server-side preview.
 *
 * `trigger()` POSTs a WebsitePreview (Rails builds the site and uploads to the
 * R2 `/preview/` dir; the Atlas worker serves it at `preview.<host>`), then we
 * poll the preview until the build completes and hand back `previewUrl` for the
 * caller to mount in an iframe. Previews are their own resource, fully separate
 * from the deploy pipeline (no Deploy row, never on a deploy surface). ~3.6s
 * warm in dev.
 */
export function useServerPreview(projectId: number | null) {
  const jwt = useJwt();
  const rootPath = useRootPath();
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [triggering, setTriggering] = useState(false);
  // Flips true once a build outlives BUILD_GIVE_UP_MS without reaching a
  // terminal status. Armed once per build (per previewId) and reset whenever a
  // fresh build starts, so a new preview always gets a clean deadline.
  const [gaveUp, setGaveUp] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${jwt ?? ""}`,
    Accept: "application/json",
  });

  // `force` skips the server-side reuse check. Pass it on the agent-done edge —
  // the site definitely changed, and the reuse shasum check can otherwise race
  // the agent's just-committed write and hand back a stale build. A plain
  // mount/reload calls trigger() with no force so reuse can short-circuit.
  const trigger = useCallback(async (force = false) => {
    if (!projectId) return;
    setTriggering(true);
    try {
      const res = await fetch(`${rootPath ?? ""}/api/v1/previews`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId, force }),
      });
      const data: Preview = await res.json();
      setPreviewId(data.id ?? null);
    } finally {
      setTriggering(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, rootPath, jwt]);

  const { data } = useQuery<Preview>({
    queryKey: ["server-preview", previewId],
    enabled: !!previewId,
    queryFn: async () => {
      const r = await fetch(`${rootPath ?? ""}/api/v1/previews/${previewId}`, {
        headers: authHeaders(),
      });
      return r.json();
    },
    refetchInterval: (q) => {
      if (gaveUp) return false;
      const s = q.state.data?.status;
      return s === "completed" || s === "failed" ? false : 2000;
    },
  });

  const rawStatus = data?.status;
  const isTerminal = rawStatus === "completed" || rawStatus === "failed";

  // Arm the give-up deadline while a build is genuinely in flight; disarm the
  // moment it reaches a terminal status. A timer left running past completion
  // fires silently minutes later, and its stale `gaveUp` would leak into the
  // NEXT build's first render (previewId changes before the reset effect runs),
  // synthesizing a "failed" status for a build that never failed.
  useEffect(() => {
    if (!previewId || isTerminal) return;
    setGaveUp(false);
    const timer = setTimeout(() => setGaveUp(true), giveUpMs());
    return () => clearTimeout(timer);
  }, [previewId, isTerminal]);

  // Treat a gave-up build as failed so the driver routes it to the error card
  // instead of spinning forever. A real terminal status always wins. The
  // `rawStatus !== undefined` guard covers the one-render window after a
  // previewId change where this preview's poll data hasn't arrived yet and
  // `gaveUp` may still hold the PREVIOUS build's verdict — never fail a build
  // we haven't heard about.
  const status = gaveUp && !isTerminal && rawStatus !== undefined ? "failed" : rawStatus;
  if (import.meta.env.DEV && gaveUp && rawStatus === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      `[GIVEUP-DEBUG] stale gaveUp observed while preview ${previewId} has no poll data — ` +
        `guard held, NOT synthesizing "failed".`,
    );
  }
  const previewUrl =
    status === "completed" ? data?.preview_url ?? undefined : undefined;
  const buildError =
    status === "failed"
      ? data?.build_error ?? (gaveUp ? GIVE_UP_MESSAGE : undefined)
      : undefined;

  return { trigger, triggering, status, previewUrl, buildError, previewId };
}
